// ---------------------------------------------------------------------------
// TanStack Query hooks for Client Overview data fetching
// Each hook wraps useQuery with proper typing, stale time, and error handling
// ---------------------------------------------------------------------------

import { useQuery, UseQueryResult } from "@tanstack/react-query";
import axios from "axios";

import type {
  PortfolioKpisData,
  EntityAllocationsResponse,
  LinkedDealsResponse,
  TasksSummaryData,
  NavTimeSeriesResponse
} from "types/clientOverview";

const API_BASE = "https://fo1.api.altius.local/api/v0.0.3";

/**
 * Generic fetch helper that returns the response.data.data (Altius envelope).
 */
async function fetchFromApi<T>(url: string): Promise<T> {
  const response = await axios.get<{ data: T }>(url);
  return response.data.data;
}

/**
 * Fetch portfolio KPIs for a given client.
 * Returns NAV, IRR, capital invested, and weighted return.
 * Stale time: 5 minutes to reduce redundant network calls.
 */
export function usePortfolioKpis(clientId: number): UseQueryResult<PortfolioKpisData> {
  return useQuery<PortfolioKpisData>({
    queryKey: ["client", clientId, "overview", "portfolio-kpis"],
    queryFn: () => fetchFromApi<PortfolioKpisData>(
      `${API_BASE}/clients/${clientId}/overview/portfolio-kpis`
    ),
    enabled: clientId > 0,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}

/**
 * Fetch entity allocations for a given client.
 * Returns up to 4 entities with allocation percentages.
 */
export function useEntityAllocations(clientId: number): UseQueryResult<EntityAllocationsResponse> {
  return useQuery<EntityAllocationsResponse>({
    queryKey: ["client", clientId, "overview", "entity-allocations"],
    queryFn: () => fetchFromApi<EntityAllocationsResponse>(
      `${API_BASE}/clients/${clientId}/overview/entity-allocations`
    ),
    enabled: clientId > 0,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}

/**
 * Fetch linked deals for a given client.
 * Returns up to 5 deals ordered by NAV descending.
 */
export function useLinkedDeals(clientId: number): UseQueryResult<LinkedDealsResponse> {
  return useQuery<LinkedDealsResponse>({
    queryKey: ["client", clientId, "overview", "linked-deals"],
    queryFn: () => fetchFromApi<LinkedDealsResponse>(
      `${API_BASE}/clients/${clientId}/overview/linked-deals`
    ),
    enabled: clientId > 0,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}

/**
 * Fetch tasks summary for a given client.
 * Returns open task count and up to 5 recent tasks.
 */
export function useTasksSummary(clientId: number): UseQueryResult<TasksSummaryData> {
  return useQuery<TasksSummaryData>({
    queryKey: ["client", clientId, "overview", "tasks-summary"],
    queryFn: () => fetchFromApi<TasksSummaryData>(
      `${API_BASE}/clients/${clientId}/overview/tasks-summary`
    ),
    enabled: clientId > 0,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}

/**
 * Fetch NAV time series for a given client.
 * Returns the combined overview endpoint which includes nav_over_time.
 * This endpoint provides historical NAV data points (date + value pairs).
 */
export function useNavTimeSeries(clientId: number): UseQueryResult<NavTimeSeriesResponse> {
  return useQuery<NavTimeSeriesResponse>({
    queryKey: ["client", clientId, "overview", "nav-time-series"],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE}/clients/${clientId}/overview`);
      return { nav_over_time: response.data.nav_over_time || [] };
    },
    enabled: clientId > 0,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}
